import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Reveal from '../components/Reveal';
import CountStepper from '../components/order/CountStepper';
import { getCatalog } from '../api';
import { fitCount, layoutOptions, MAX_SPOTS } from '../boardLayout';
import './Order.css';
import './Pricing.css';

// What a Mosaik costs, kept short and laid out like printed matter - three
// paper cards and an ink ticket, with type running up their edges: the board (small or big, by how many
// people join), the canvases and the delivery - then the lowest price for a
// group of your size. No price is written down here: everything comes from
// /api/catalog, the same place the order page reads it, so this page can't
// drift from what is charged. The "from" price is the cheapest a group can
// get: the lowest canvas price, collected in person.

const DELIVERIES = ['pickup', 'ship_to_curator', 'ship_split'];

function Pricing() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === 'en' ? 'en-GB' : 'nl-NL';
  const money = (cents) => new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(cents / 100);

  const [catalog, setCatalog] = useState(null);
  useEffect(() => {
    getCatalog().then(setCatalog).catch(() => setCatalog(false));
  }, []);

  const sizes = useMemo(() => [...(catalog?.canvas_sizes ?? [])].sort((a, b) => a.size_cm - b.size_cm), [catalog]);
  const sheets = useMemo(
    () => [...(catalog?.boards ?? [])].sort((a, b) => a.width_cm * a.height_cm - b.width_cm * b.height_cm),
    [catalog]
  );
  const pricing = catalog?.pricing;
  const sizeLabel = (s) => (i18n.resolvedLanguage === 'nl' && s?.label_nl) || s?.label;

  // How many people each board takes, at the smallest canvas size: the small
  // sheet alone, then every sheet.
  const baseSize = sizes[0] ?? null;
  const smallCap = baseSize && sheets.length ? fitCount(MAX_SPOTS, baseSize.size_cm, [sheets[0]]) : 0;
  const bigCap = baseSize && sheets.length ? fitCount(MAX_SPOTS, baseSize.size_cm, sheets) : 0;
  const twoBoards = sheets.length > 1;

  // The cheapest a group can get: the lowest canvas price, collected.
  const [people, setPeople] = useState(16);
  const cheapest = useMemo(
    () => (sizes.length ? sizes.reduce((a, b) => (b.price_cents < a.price_cents ? b : a)) : null),
    [sizes]
  );
  const from = useMemo(() => {
    if (!cheapest || !pricing) return null;
    const shape = layoutOptions(people, cheapest.size_cm, sheets)[0];
    if (!shape) return { tooBig: true };
    const canvases = shape.cols * shape.rows;
    return { total: shape.sheet.price_cents + canvases * cheapest.price_cents + pricing.pickup_fee_cents };
  }, [people, cheapest, sheets, pricing]);

  const deliveryPrice = (id) => {
    if (id === 'pickup') return pricing.pickup_fee_cents === 0 ? t('order.delivery.free') : money(pricing.pickup_fee_cents);
    return money(pricing.ship_to_curator_fee_cents);
  };

  // The three price cards: what's printed up one edge, down the other, and
  // in the middle.
  const cards = [
    {
      key: 'board',
      rows: [
        sheets.length > 0 && {
          name: t(twoBoards ? 'pricing.board.small' : 'pricing.board.one'),
          note: t('pricing.board.upTo', { count: smallCap }),
          price: money(sheets[0].price_cents),
        },
        twoBoards && {
          name: t('pricing.board.big'),
          note: t('pricing.board.upTo', { count: bigCap }),
          price: money(sheets[sheets.length - 1].price_cents),
        },
      ].filter(Boolean),
    },
    {
      key: 'people',
      rows: sizes.map((s) => ({
        name: sizeLabel(s),
        note: `${s.size_cm} × ${s.size_cm} cm`,
        price: money(s.price_cents),
      })),
    },
    {
      key: 'shipping',
      rows: pricing
        ? DELIVERIES.map((id) => ({
            name: t(`order.delivery.options.${id}.title`),
            note: id === 'ship_split' ? t('pricing.shipping.perCanvas', { price: money(pricing.canvas_postage_cents) }) : null,
            price: deliveryPrice(id),
          }))
        : [],
    },
  ];

  return (
    <div className="pr">
      {/* Roughens the ticket's edges into something closer to a print. */}
      <svg className="pr-defs" aria-hidden="true" focusable="false">
        <filter id="pr-rough">
          <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="3" seed="7" />
          <feDisplacementMap in="SourceGraphic" scale="7" />
        </filter>
        <filter id="pr-brush" x="-10%" y="-10%" width="120%" height="120%">
          <feTurbulence type="fractalNoise" baseFrequency="0.02 0.06" numOctaves="2" seed="3" />
          <feDisplacementMap in="SourceGraphic" scale="7" />
        </filter>
      </svg>

      {/* Two loose strokes of ink behind everything. */}
      <svg className="pr-stroke pr-stroke--top" viewBox="0 0 600 420" aria-hidden="true" focusable="false">
        <path d="M30 330 C 90 120, 300 20, 420 110 S 560 330, 470 380" />
        <path d="M250 200 C 280 170, 330 170, 350 205" />
      </svg>
      <svg className="pr-stroke pr-stroke--bottom" viewBox="0 0 600 420" aria-hidden="true" focusable="false">
        <path d="M40 60 C 180 10, 420 40, 520 170 S 420 400, 200 380" />
      </svg>

      <div className="pr-inner h-container">
        <header className="pr-head">
          <Reveal as="p" className="h-label">
            {t('pricing.kicker')}
          </Reveal>
          <Reveal as="h1" className="h-display pr-title" delay={100}>
            {t('pricing.title')}
          </Reveal>
          <Reveal as="p" className="pr-lede" delay={200}>
            {t('pricing.lede')}
          </Reveal>
        </header>

        {catalog === false && <p className="pr-note">{t('pricing.unavailable')}</p>}

        {/* Three cards, laid on the table. */}
        <div className="pr-cards">
          {cards.map((card, index) => (
            <Reveal
              as="article"
              className={`pr-card pr-card--${index + 1}${card.rows.length === 1 ? ' pr-card--single' : ''}`}
              key={card.key} delay={index * 110}
            >
              <span className="pr-side pr-side--left" aria-hidden="true">
                {String(index + 1).padStart(2, '0')} &nbsp; {t(`pricing.${card.key}.title`)}
              </span>
              <div className="pr-card-main">
                <h2>{t(`pricing.${card.key}.title`)}</h2>
                <p className="pr-card-text">
                  {card.rows.length === 1 && card.key !== 'shipping'
                    ? t(`pricing.${card.key}.textOne`)
                    : t(`pricing.${card.key}.text`)}
                </p>
                <ul>
                  {card.rows.map((row) => (
                    <li key={row.name}>
                      <span className="pr-row-name">{row.name}</span>
                      <strong>{row.price}</strong>
                      {row.note && <small>{row.note}</small>}
                    </li>
                  ))}
                </ul>
              </div>
              <span className="pr-side pr-side--right" aria-hidden="true">
                {t(`pricing.${card.key}.per`)}
              </span>
            </Reveal>
          ))}
        </div>

        {/* The ticket: how many people, and the lowest price for them. */}
        {cheapest && pricing && (
          <Reveal as="section" className="pr-ticket-wrap">
            <div className="pr-ticket">
              <span className="pr-ticket-bg" aria-hidden="true" />
              <span className="pr-side pr-side--left" aria-hidden="true">
                {t('pricing.try.people')}
              </span>
              <div className="pr-ticket-main">
                <div className="pr-ticket-ask">
                  <CountStepper
                    value={people}
                    min={1}
                    max={MAX_SPOTS}
                    onChange={setPeople}
                    lessLabel={t('order.board.count.less')}
                    moreLabel={t('order.board.count.more')}
                    inputLabel={t('order.board.count.label')}
                  />
                </div>
                <div className="pr-ticket-answer" aria-live="polite" aria-label={t('pricing.try.from')}>
                  {from?.tooBig ? (
                    <p>{t('pricing.try.tooBig')}</p>
                  ) : (
                    from && (
                      <>
                        <p className="pr-total">{money(from.total)}</p>
                        <p className="pr-note">
                          {t('pricing.try.each', { price: money(Math.round(from.total / people)) })} &middot;{' '}
                          {t('pricing.try.basis', { size: cheapest.size_cm })}
                        </p>
                      </>
                    )
                  )}
                </div>
              </div>
              <span className="pr-side pr-side--right" aria-hidden="true">
                {t('pricing.try.from')}
              </span>
            </div>
          </Reveal>
        )}

        <Reveal className="pr-cta">
          <Link to="/order" className="h-link">
            {t('home.cta')} <span aria-hidden="true">&rarr;</span>
          </Link>
        </Reveal>
      </div>
    </div>
  );
}

export default Pricing;
